import { EventCategory } from "../types";

/**
 * Event Helper Functions
 * Utilities for event categorization, formatting, and display
 */

// UNIFIED COLOR SYSTEM: Same category = Same color across Offers & Events
// Event category configurations
export const eventCategoryConfigs: Record<
  EventCategory,
  {
    icon: string;
    label: string;
    color: string;
    gradient: string;
  }
> = {
  music: {
    icon: "🎵",
    label: "Music Concert",
    color: "#8e44ad",
    gradient: "linear-gradient(135deg, #8e44ad 0%, #c39bd3 100%)", // Rich purple
  },
  food: {
    icon: "🍕",
    label: "Food Festival",
    color: "#e67e22",
    gradient: "linear-gradient(135deg, #e67e22 0%, #f39c12 100%)", // Orange (UNIFIED with Offers)
  },
  art: {
    icon: "🎨",
    label: "Art & Culture",
    color: "#c0392b",
    gradient: "linear-gradient(135deg, #c0392b 0%, #e74c3c 100%)", // Deep red
  },
  kids: {
    icon: "🎈",
    label: "Kids Fun Zone",
    color: "#27ae60",
    gradient: "linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)", // Vibrant green
  },
  fashion: {
    icon: "👗",
    label: "Fashion Show",
    color: "#ec008c",
    gradient: "linear-gradient(135deg, #ec008c 0%, #fc6767 100%)", // Hot pink (matches clothing)
  },
  sale: {
    icon: "🏷️",
    label: "Sale Event",
    color: "#e74c3c",
    gradient: "linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)", // Bold red
  },
  workshop: {
    icon: "🛠️",
    label: "Workshop",
    color: "#3498db",
    gradient: "linear-gradient(135deg, #3498db 0%, #2980b9 100%)", // Deep blue
  },
  entertainment: {
    icon: "🎭",
    label: "Entertainment",
    color: "#9b59b6",
    gradient: "linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)", // Purple (UNIFIED with Offers)
  },
  sports: {
    icon: "⚽",
    label: "Sports Event",
    color: "#16a085",
    gradient: "linear-gradient(135deg, #16a085 0%, #1abc9c 100%)", // Teal (UNIFIED with Offers)
  },
  tech: {
    icon: "💻",
    label: "Tech Event",
    color: "#34495e",
    gradient: "linear-gradient(135deg, #34495e 0%, #2c3e50 100%)", // Steel gray (UNIFIED with Offers)
  },
  beauty: {
    icon: "💄",
    label: "Beauty & Wellness",
    color: "#f857a6",
    gradient: "linear-gradient(135deg, #f857a6 0%, #ff5858 100%)", // Pink-coral (UNIFIED with Offers)
  },
  health: {
    icon: "🧘",
    label: "Health & Fitness",
    color: "#06beb6",
    gradient: "linear-gradient(135deg, #06beb6 0%, #48b1bf 100%)", // Aqua (UNIFIED with Offers)
  },
  photography: {
    icon: "📸",
    label: "Photography",
    color: "#1a237e",
    gradient: "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)", // Deep navy-blue
  },
  dance: {
    icon: "💃",
    label: "Dance Performance",
    color: "#ff6b95",
    gradient: "linear-gradient(135deg, #ff6b95 0%, #feca57 100%)", // Pink-yellow vibrant
  },
  fitness: {
    icon: "🏋️",
    label: "Fitness Challenge",
    color: "#f2994a",
    gradient: "linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)", // Warm amber
  },
  comedy: {
    icon: "😂",
    label: "Comedy Show",
    color: "#feca57",
    gradient: "linear-gradient(135deg, #feca57 0%, #ff9ff3 100%)", // Yellow-pink cheerful
  },
  movie: {
    icon: "🎬",
    label: "Movie Screening",
    color: "#89216b",
    gradient: "linear-gradient(135deg, #89216b 0%, #da4453 100%)", // Deep rose
  },
  celebrity: {
    icon: "⭐",
    label: "Celebrity Meet",
    color: "#d4af37",
    gradient: "linear-gradient(135deg, #d4af37 0%, #ffd700 100%)", // Gold (star power)
  },
};

// Get category config
export const getCategoryConfig = (category: EventCategory) => {
  return eventCategoryConfigs[category] || eventCategoryConfigs.entertainment;
};

// Get category gradient for visual styling
export const getCategoryGradient = (category: EventCategory): string => {
  const config = eventCategoryConfigs[category];
  return config?.gradient || eventCategoryConfigs.entertainment.gradient;
};

// Format date for display
export const formatEventDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

// Format time for display
export const formatEventTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Format date range
export const formatEventDateRange = (
  startDate: string,
  endDate: string,
): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const isSameDay = start.toDateString() === end.toDateString();

  if (isSameDay) {
    return formatEventDate(startDate);
  }

  return `${formatEventDate(startDate)} - ${formatEventDate(endDate)}`;
};

// Get event status badge info
// check again and test all scenarios
export const getEventStatusBadge = (
  startDate: string,
  endDate: string,
  status: string,
) => {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (status === "cancelled") {
    return { label: "Cancelled", color: "error", icon: "❌" };
  }

  if (status === "completed") {
    return { label: "Completed", color: "default", icon: "⚪" };
  }

  // Check if event is today
  const isToday = start.toDateString() === now.toDateString();
  if (isToday) {
    return { label: "Today", color: "error", icon: "🔴" };
  }

  // Check if event is ongoing
  if (now >= start && now <= end) {
    return { label: "Happening Now", color: "warning", icon: "🟠" };
  }

  // Check if event is within 7 days
  const daysUntil = Math.ceil(
    (start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (daysUntil <= 7 && daysUntil >= 0) {
    return { label: "Happening Soon", color: "success", icon: "🟢" };
  }

  return { label: "Upcoming", color: "info", icon: "📅" };
};

// Check if event is happening today
export const isEventToday = (startDate: string): boolean => {
  const now = new Date();
  const start = new Date(startDate);
  return start.toDateString() === now.toDateString();
};

// Check if event is happening this week
export const isEventThisWeek = (startDate: string): boolean => {
  const now = new Date();
  const start = new Date(startDate);
  const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return start >= now && start <= weekFromNow;
};

// Get entry type display
export const getEntryTypeDisplay = (
  entryType: string,
  ticketPrice?: number,
): string => {
  switch (entryType) {
    case "free":
      return "Free Entry";
    case "paid":
      return ticketPrice ? `₹${ticketPrice}` : "Paid Entry";
    case "registration_required":
      return "Registration Required";
    default:
      return "Check Details";
  }
};
